import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import {
  InlineResponse200,
  InlineResponse200Results,
  ReportsService,
} from '../../api/generated';

@Injectable({
  providedIn: 'root',
})
export class AccessLogsService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  public loading$ = this.loadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  constructor(private reportsService: ReportsService) {}

  loadAccessLogs(
    page: number = 1,
    pageSize: number = 50
  ): Observable<InlineResponse200> {
    this.setLoading(true);
    this.clearError();

    return new Observable((observer) => {
      this.reportsService.reportsLogsAccessList(page, pageSize).subscribe({
        next: (response) => {
          this.setLoading(false);
          observer.next(response);
          observer.complete();
        },
        error: (err) => {
          console.error('Error loading access logs:', err);
          this.setError('Fehler beim Laden der Zugriffsprotokolle');
          this.setLoading(false);
          observer.error(err);
        },
      });
    });
  }

  exportAccessLogs(): Observable<void> {
    this.setLoading(true);
    this.clearError();

    return new Observable((observer) => {
      this.reportsService
        .reportsLogsAccessExportCsvList()
        .subscribe({
          next: (csvContent: string) => {
            this.downloadCSV(csvContent);
            this.setLoading(false);
            observer.next();
            observer.complete();
          },
          error: (err) => {
            console.error('Error exporting access logs:', err);
            this.setError('Fehler beim Exportieren der Zugriffsprotokolle');
            this.setLoading(false);
            observer.error(err);
          },
        });
    });
  }

  private downloadCSV(csvContent: string): void {
    // Create blob from the CSV content
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8',
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `access_protocol_reports_${new Date()
      .toISOString()
      .slice(0, 10)}_${new Date()
      .toTimeString()
      .slice(0, 8)
      .replace(/:/g, '')}.csv`;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  getDisplayName(log: InlineResponse200Results): string {
    if (log.user_first_name && log.user_last_name) {
      return `${log.user_first_name} ${log.user_last_name}`;
    }
    if (log.user_username) {
      return log.user_username;
    }
    if (log.user_email) {
      return log.user_email;
    }
    return 'Unbekannter Benutzer';
  }

  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  private setError(error: string): void {
    this.errorSubject.next(error);
  }

  private clearError(): void {
    this.errorSubject.next(null);
  }

  clearErrorState(): void {
    this.clearError();
  }
}
